import type React from "react"
import { useState, useEffect } from "react"
import { SettingsIcon, Globe, Palette, Building } from 'lucide-react'
import * as Yup from "yup"
import GeneralSection from "./GeneralSection"
import WebsiteSection from "./WebsiteSection"
import AppSection from "./AppSection"
import { CompanyDetailsSection } from "./CompanyDetailsSection"
import { useSettings } from "../../../src/context/SettingsContext"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
 
export interface FormData {
  logoName: string
  logoPreview: string
  mailMailer: string
  mailHost: string
  mailPort: string
  emailAddress: string
  name: string
  emailPassword: string
  sidebarColor: string
  headerColor: string
  menuActiveColor: string
  fontColor: string
}

export interface Errors {
  [key: string]: string
}

const generalSchema = Yup.object({
  logoName: Yup.string(),
  logoPreview: Yup.string(),
})

const websiteSchema = Yup.object({
  mailMailer: Yup.string().required("Mail Mailer is required"),
  mailHost: Yup.string()
    .required("Mail Host is required")
    .matches(/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid: Mail Host format incorrect"),
  mailPort: Yup.string()
    .required("Mail Port is required")
    .matches(/^\d+$/, "Invalid: Mail Port must be a number")
    .test("port-range", "Invalid: Mail Port must be between 1 and 65535", (value) => {
      const num = Number.parseInt(value, 10)
      return num >= 1 && num <= 65535
    }),
  emailAddress: Yup.string().required("Email Address is required").email("Invalid: Email Address format incorrect"),
  emailPassword: Yup.string()
    .required("Email Password is required")
    .min(6, "Invalid: Email Password must be at least 6 characters"),
  name: Yup.string().required("Name is required").min(2, "Invalid: Name must be at least 2 characters"),
})

const appSchema = Yup.object({
  sidebarColor: Yup.string()
    .required("Sidebar Color is required")
    .matches(/^#[0-9A-Fa-f]{6}$/, "Invalid: Sidebar Color must be a valid hex code (e.g., #FFFFFF)"),
  headerColor: Yup.string()
    .required("Header Color is required")
    .matches(/^#[0-9A-Fa-f]{6}$/, "Invalid: Header Color must be a valid hex code (e.g., #FFFFFF)"),
  menuActiveColor: Yup.string()
    .required("Menu Active Color is required")
    .matches(/^#[0-9A-Fa-f]{6}$/, "Invalid: Menu Active Color must be a valid hex code (e.g., #FFFFFF)"),
  fontColor: Yup.string()
    .required("Font Color is required")
    .matches(/^#[0-9A-Fa-f]{6}$/, "Invalid: Font Color must be a valid hex code (e.g., #FFFFFF)"),
})

const SettingList: React.FC = () => {
  const { settings, updateSettings, updateLogo, updateEmailSettings, updateThemeColors } = useSettings()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<string>("general")
  const [showEmailPassword, setShowEmailPassword] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [formData, setFormData] = useState<FormData>({
    logoName: settings.logoName || "",
    logoPreview: settings.logoPath || "",
    mailMailer: settings.mailMailer || "",
    mailHost: settings.mailHost || "",
    mailPort: settings.mailPort || "",
    emailAddress: settings.email || "",
    emailPassword: settings.password || "",
    name: settings.name || "",
    sidebarColor: settings.sidebarColor || "#ffffff",
    headerColor: settings.headerColor || "#ffffff",
    menuActiveColor: settings.menuActiveColor || "#ede9e9",
    fontColor: settings.fontColor || "#000000",
  })
  const [errors, setErrors] = useState<Errors>({})

  const token = sessionStorage.getItem("access_token")
  const updateFavicon = (url: string) => {
  const favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (favicon) favicon.href = `${url}?v=${Date.now()}`; // prevent browser cache
}

  useEffect(() => {
    if (!token) {
      toast.dismiss()
      toast.error("Authorization required. Please log in.")
      navigate("/signin")
    }
  }, [token, navigate])

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      if (value) localStorage.setItem(key, value)
      else localStorage.removeItem(key)
    })
  }, [formData])

  const tabs = [
    { id: "general", label: "General Setting", icon: SettingsIcon },
    { id: "website", label: "Email Setting", icon: Globe },
    { id: "app", label: "App Setting", icon: Palette },
    { id: "company", label: "Company Details", icon: Building },
  ]

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.dismiss()
        toast.error("File size must be less than 2MB")
        return
      }
      setLogoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = (e.target as FileReader)?.result
        if (typeof result === "string") {
          setFormData((prev) => ({ ...prev, logoPreview: result }))
          validateField("logoPreview", result)
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const validateField = async (name: string, value: string) => {
    try {
      const schema = activeTab === "general" ? generalSchema : activeTab === "website" ? websiteSchema : appSchema
      await schema.validateAt(name, { [name]: value })
      setErrors((prev) => ({ ...prev, [name]: "" }))
    } catch (error: unknown) {
      if (error instanceof Yup.ValidationError) {
        const errorMessage = error.message || "Validation error occurred"
        setErrors((prev) => ({ ...prev, [name]: errorMessage }))
      } else {
        const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred"
        setErrors((prev) => ({ ...prev, [name]: errorMessage }))
      }
    }
  }

  const validateForm = async (): Promise<boolean> => {
    try {
      const schema = activeTab === "general" ? generalSchema : activeTab === "website" ? websiteSchema : appSchema
      await schema.validate(formData, { abortEarly: false })
      setErrors({})
      return true
    } catch (error: unknown) {
      if (error instanceof Yup.ValidationError) {
        const newErrors: Errors = {}
        error.inner.forEach((err: Yup.ValidationError) => {
          if (err.path) newErrors[err.path] = err.message || "Validation error occurred"
        })
        setErrors(newErrors)
        return false
      }
      return false
    }
  }

  const hasChanges = () => {
    if (activeTab === "general") {
      return (
        formData.logoName !== (settings.logoName || "") ||
        formData.logoPreview !== (settings.logoPath || "") ||
        logoFile !== null
      )
    } else if (activeTab === "website") {
      return (
        formData.mailMailer !== (settings.mailMailer || "") ||
        formData.mailHost !== (settings.mailHost || "") ||
        formData.mailPort !== (settings.mailPort || "") ||
        formData.name !== (settings.name || "") ||
        formData.emailAddress !== (settings.email || "") ||
        formData.emailPassword !== (settings.password || "")
      )
    } else if (activeTab === "app") {
      return (
        formData.sidebarColor !== (settings.sidebarColor || "#ffffff") ||
        formData.headerColor !== (settings.headerColor || "#ffffff") ||
        formData.menuActiveColor !== (settings.menuActiveColor || "#ede9e9") ||
        formData.fontColor !== (settings.fontColor || "#000000")
      )
    }
    
    return false
  }

  const handleSubmit = async () => {
    if (!token) {
      toast.dismiss()
      toast.error("Authorization required. Please log in.")
      navigate("/signin")
      return
    }

    if (!(await validateForm())) {
      toast.dismiss()
      toast.error("Please fix the errors before saving")
      return
    }

    if (!hasChanges()) {
      toast.dismiss()
      toast.success("No changes detected to save")
      return
    }

    setIsLoading(true)

    try {
if (activeTab === "general") {
  if (!settings.settingId) {
    toast.dismiss()
    toast.error("Setting ID is missing")
    setIsLoading(false)
    return
  }

  const success = await updateLogo(settings.settingId, logoFile, formData.logoName)
  if (success) {
    // Update favicon immediately if a new preview exists
    if (formData.logoPreview) {
      updateFavicon(formData.logoPreview)
    }

    updateSettings({
      logo: logoFile,
      logoPath: formData.logoPreview,
      email: formData.emailAddress,
      name: formData.name,
      password: formData.emailPassword,
      sidebarColor: formData.sidebarColor,
      headerColor: formData.headerColor,
      menuActiveColor: formData.menuActiveColor,
      fontColor: formData.fontColor,
      settingId: settings.settingId,
      logoName: formData.logoName,
      mailMailer: formData.mailMailer,
      mailHost: formData.mailHost,
      mailPort: formData.mailPort,
    })

    toast.dismiss()
    toast.success("General settings saved successfully!")
  } else {
    throw new Error("Failed to save general settings")
  }
}
 else if (activeTab === "website") {
        if (!settings.settingId) {
          toast.dismiss()
          toast.error("Setting ID is missing")
          setIsLoading(false)
          return
        }

        const success = await updateEmailSettings(settings.settingId, {
          email: formData.emailAddress,
          password: formData.emailPassword,
          mailMailer: formData.mailMailer,
          mailHost: formData.mailHost,
          mailPort: formData.mailPort,
          name: formData.name,
        })

        if (success) {
          updateSettings({
            logo: logoFile,
            logoPath: formData.logoPreview,
            email: formData.emailAddress,
            name: formData.name, // Add name
            password: formData.emailPassword,
            sidebarColor: formData.sidebarColor,
            headerColor: formData.headerColor,
            menuActiveColor: formData.menuActiveColor,
            fontColor: formData.fontColor,
            settingId: settings.settingId,
            logoName: formData.logoName,
            mailMailer: formData.mailMailer,
            mailHost: formData.mailHost,
            mailPort: formData.mailPort,
          })
          toast.dismiss()
          toast.success("Email settings saved successfully!")
        } else {
          throw new Error("Failed to save email settings")
        }
      } else if (activeTab === "app") {
        if (!settings.settingId) {
          toast.dismiss()
          toast.error("Setting ID is missing")
          setIsLoading(false)
          return
        }

        const success = await updateThemeColors(settings.settingId, {
          sidebarColor: formData.sidebarColor,
          headerColor: formData.headerColor,
          fontColor: formData.fontColor,
          menuActiveColor: formData.menuActiveColor,
        })

        if (success) {
          updateSettings({
            logo: logoFile,
            logoPath: formData.logoPreview,
            email: formData.emailAddress,
            name: formData.name, // Add name
            password: formData.emailPassword,
            sidebarColor: formData.sidebarColor,
            headerColor: formData.headerColor,
            menuActiveColor: formData.menuActiveColor,
            fontColor: formData.fontColor,
            settingId: settings.settingId,
            logoName: formData.logoName,
            mailMailer: formData.mailMailer,
            mailHost: formData.mailHost,
            mailPort: formData.mailPort,
          })
          toast.dismiss()
          toast.success("App settings saved successfully!")
        } else {
          throw new Error("Failed to save app settings")
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || "An error occurred while saving settings"
      toast.dismiss()
      toast.error(errorMessage)
      if (activeTab === "general") {
        setErrors((prev) => ({
          ...prev,
          company_logo: errorMessage.includes("company_logo") ? errorMessage : prev.company_logo,
        }))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = async () => {
    if (!token) {
      toast.dismiss()
      toast.error("Authorization required. Please log in.")
      navigate("/signin")
      return
    }

    setIsLoading(true)

    try {
      if (activeTab === "general") {
        // Reset to empty values for general section
        const defaults: Partial<FormData> = {
          logoName: "",
          logoPreview: "",
        }

        // Update local form data
        setFormData((prev) => ({ ...prev, ...defaults }))
        setLogoFile(null)
        setErrors({})

        // If we have a settingId, also clear from API
        if (settings.settingId) {
          const success = await updateLogo(settings.settingId, null, "")

        if (success) {
          // updateSettings with cache-busting logoPath
          updateSettings({
            ...settings,
            logo: null,
            logoPath: `${settings.logoPath}?v=${Date.now()}`,
            logoName: "",
          })
          toast.dismiss()
          toast.success("General settings reset and cleared from server successfully!")
        } else {
            throw new Error("Failed to clear logo data from server")
          }
        } else {
          toast.dismiss()
          toast.success("General settings reset to default")
        }
      } else if (activeTab === "app") {
        // Reset to specific default colors for app section
        const defaults: Partial<FormData> = {
          sidebarColor: "#ffffff",
          headerColor: "#ffffff",
          menuActiveColor: "#ede9e9",
          fontColor: "#000000",
        }

        // Update form data
        setFormData((prev) => ({ ...prev, ...defaults }))
        setErrors({})

        // If we have a settingId, also update via API
        if (settings.settingId) {
          const success = await updateThemeColors(settings.settingId, {
            sidebarColor: defaults.sidebarColor!,
            headerColor: defaults.headerColor!,
            fontColor: defaults.fontColor!,
            menuActiveColor: defaults.menuActiveColor!,
          })

          if (success) {
            updateSettings({
              ...settings,
              sidebarColor: defaults.sidebarColor!,
              headerColor: defaults.headerColor!,
              menuActiveColor: defaults.menuActiveColor!,
              fontColor: defaults.fontColor!,
            })
            toast.dismiss()
            toast.success("App settings reset to default and saved successfully!")
          } else {
            throw new Error("Failed to save reset values to server")
          }
        } else {
          toast.dismiss()
          toast.success("App settings reset to default")
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || "An error occurred while resetting settings"
      toast.dismiss()
      toast.error(errorMessage)

      // If there was an error with the API call, we might want to revert the local changes
      if (activeTab === "general") {
        // Revert to original values if API call failed
        setFormData((prev) => ({
          ...prev,
          logoName: settings.logoName || "",
          logoPreview: settings.logoPath || "",
        }))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    validateField(field, value)
  }

  return (
    <div className="w-full bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 flex flex-col relative">
      <div className="w-full max-w-7xl mx-auto">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1">Settings</h1>
          <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm">Customize your application preferences</p>
        </div>

        <div className="mb-4 sm:mb-6 sticky top-0 z-10">
          <div className="flex flex-col sm:flex-row gap-2 p-2 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex items-center justify-center px-4 py-2 font-semibold transition-all duration-300 text-sm sm:text-base flex-1 sm:flex-none ${activeTab === tab.id
                    ? "text-blue-500"
                    : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    }`}
                >
                  <Icon className={`w-5 h-5 mr-2 ${activeTab === tab.id ? "text-blue-500" : ""}`} />
                  {tab.label}
                  {activeTab === tab.id && (
                    <span className="absolute bottom-0 left-0 w-full h-1 bg-blue-500 animate-pulse" />
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          {activeTab === "general" && (
            <GeneralSection
              formData={formData}
              errors={errors}
              handleInputChange={handleInputChange}
              handleImageUpload={handleImageUpload}
              handleReset={handleReset}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
            />
          )}
          {activeTab === "website" && (
            <WebsiteSection
              formData={formData}
              errors={errors}
              handleInputChange={handleInputChange}
              showEmailPassword={showEmailPassword}
              setShowEmailPassword={setShowEmailPassword}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
            />
          )}
          {activeTab === "app" && (
            <AppSection
              formData={formData}
              errors={errors}
              handleInputChange={handleInputChange}
              handleReset={handleReset}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
            />
          )}
          {activeTab === "company" && (
            <CompanyDetailsSection />
          )}
        </div>
      </div>
    </div>
  )
}

export default SettingList   